import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, MessageSquare, PenTool, ArrowRight, Search, Eye, Phone,
  Clock, CheckCircle, AlertCircle, User, Calendar, X, Edit, Save,
  Users, Send, Archive, RefreshCw, ChevronDown, ChevronUp, History, Trash2, Loader
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { WhatsAppButton } from '../components/WhatsAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending', label: 'قيد الانتظار', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'in_progress', label: 'قيد المعالجة', color: 'bg-blue-100 text-blue-700' },
  { value: 'under_review', label: 'تحت المراجعة', color: 'bg-purple-100 text-purple-700' },
  { value: 'transferred', label: 'محال', color: 'bg-orange-100 text-orange-700' },
  { value: 'completed', label: 'مكتمل', color: 'bg-green-100 text-green-700' },
  { value: 'archived', label: 'مؤرشف', color: 'bg-gray-100 text-gray-700' },
  { value: 'cancelled', label: 'ملغي', color: 'bg-red-100 text-red-700' },
];

export default function ClientRequests() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [cases, setCases] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [notaryRequests, setNotaryRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cases');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [requestActions, setRequestActions] = useState([]);
  
  // حالات الإحالة
  const [actionType, setActionType] = useState('update');
  const [actionText, setActionText] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [newStatus, setNewStatus] = useState('');
  const [savingAction, setSavingAction] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, request: null, type: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) setUser(JSON.parse(userStr));
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [requestsRes, consultationsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/client-requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/consultations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/employees?include_admins=true`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const allRequests = requestsRes.data || [];
      setCases(allRequests.filter(r => r.request_type === 'case'));
      setNotaryRequests(allRequests.filter(r => r.request_type === 'notary'));
      setConsultations(consultationsRes.data || []);
      setEmployees(employeesRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const fetchRequestActions = async (requestId, requestType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/requests/${requestType}/${requestId}/actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequestActions(response.data || []);
    } catch (err) {
      console.error('Error fetching actions:', err);
      setRequestActions([]);
    }
  };

  const openActionModal = async (request, type) => {
    setSelectedRequest({ ...request, requestType: type });
    await fetchRequestActions(request.id, type);
    setShowActionModal(true);
    setActionType('update');
    setActionText('');
    setSelectedEmployees([]);
    setNewStatus('');
  };

  // دالة حذف الطلب (للمدير فقط)
  const handleDeleteRequest = async () => {
    if (!deleteModal.request) return;
    
    setDeleting(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/client-requests/${deleteModal.request.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // تحديث القوائم
      if (deleteModal.type === 'case') {
        setCases(cases.filter(c => c.id !== deleteModal.request.id));
      } else if (deleteModal.type === 'consultation') {
        setConsultations(consultations.filter(c => c.id !== deleteModal.request.id));
      } else if (deleteModal.type === 'notary') {
        setNotaryRequests(notaryRequests.filter(n => n.id !== deleteModal.request.id));
      }
      
      setDeleteModal({ show: false, request: null, type: null });
    } catch (error) {
      console.error('Error deleting request:', error);
      alert(error.response?.data?.detail || 'حدث خطأ أثناء حذف الطلب');
    }
    setDeleting(false);
  };

  const submitAction = async () => {
    if (!actionText.trim()) {
      alert('يرجى كتابة تفاصيل الإجراء');
      return;
    }
    
    setSavingAction(true);
    try {
      const token = localStorage.getItem('token');
      const actionData = {
        action_type: actionType,
        action_text: actionText,
        new_status: newStatus || undefined,
        new_assigned_to: selectedEmployees.length > 0 ? selectedEmployees.map(e => e.id) : undefined,
        new_assigned_to_names: selectedEmployees.length > 0 ? selectedEmployees.map(e => e.full_name) : undefined
      };
      
      await axios.post(
        `${API}/requests/${selectedRequest.requestType}/${selectedRequest.id}/actions`,
        actionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // إشعار العميل عبر واتساب إذا أردت
      const phone = selectedRequest.phone_number || selectedRequest.client_phone;
      if (phone && actionType !== 'update') {
        const notify = window.confirm('هل تريد إشعار العميل بهذا الإجراء عبر واتساب؟');
        if (notify) {
          sendWhatsAppNotification(phone, selectedRequest.client_name, actionText);
        }
      }
      
      alert('تم إضافة الإجراء بنجاح');
      setShowActionModal(false);
      fetchData();
    } catch (err) {
      console.error('Error submitting action:', err);
      alert('حدث خطأ');
    }
    setSavingAction(false);
  };

  const sendWhatsAppNotification = (phone, clientName, actionText) => {
    let cleanNumber = phone.replace(/[\s\-\(\)]/g, '');
    if (cleanNumber.startsWith('05')) cleanNumber = '966' + cleanNumber.substring(1);
    else if (cleanNumber.startsWith('5')) cleanNumber = '966' + cleanNumber;
    else if (!cleanNumber.startsWith('966') && !cleanNumber.startsWith('+')) cleanNumber = '966' + cleanNumber;
    cleanNumber = cleanNumber.replace('+', '');
    
    const message = `مرحباً ${clientName}،\n\nمكتب المحامي هشام يوسف الخياط يُحييك.\n\n📋 تحديث على طلبك:\n${actionText}\n\nللاستفسار، نحن في خدمتك.`;
    window.open(`https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return <span className={`px-2 py-1 rounded-full text-xs ${opt?.color || 'bg-gray-100 text-gray-600'}`}>{opt?.label || status}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA');
  };

  const filterData = (data) => {
    if (!searchQuery) return data;
    return data.filter(item =>
      item.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.request_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.subject?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const renderTable = (data, type) => (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-right text-sm">الرقم</th>
              <th className="px-4 py-3 text-right text-sm">العميل</th>
              <th className="px-4 py-3 text-right text-sm">الجوال</th>
              <th className="px-4 py-3 text-right text-sm">{type === 'notary' ? 'نوع الخدمة' : 'الموضوع'}</th>
              <th className="px-4 py-3 text-center text-sm">التاريخ</th>
              <th className="px-4 py-3 text-center text-sm">الحالة</th>
              <th className="px-4 py-3 text-center text-sm">المحال إليه</th>
              <th className="px-4 py-3 text-center text-sm">إجراءات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filterData(data).map((item, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-mono text-gold">{item.request_number || item.consultation_number || '-'}</td>
                <td className="px-4 py-3 text-sm">{item.client_name}</td>
                <td className="px-4 py-3 text-sm" dir="ltr">{item.phone_number || '-'}</td>
                <td className="px-4 py-3 text-sm max-w-[200px] truncate">{type === 'notary' ? item.service_type : item.subject}</td>
                <td className="px-4 py-3 text-sm text-center text-gray-500">{formatDate(item.created_at)}</td>
                <td className="px-4 py-3 text-center">{getStatusBadge(item.status)}</td>
                <td className="px-4 py-3 text-center text-sm">
                  {item.assigned_to_names?.join(', ') || item.assigned_to_name || '-'}
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => { setSelectedRequest(item); setShowModal(true); }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      title="عرض التفاصيل"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openActionModal(item, type)}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                      title="إجراء / إحالة"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                    {user?.role === 'admin' && (
                      <button
                        onClick={() => setDeleteModal({ show: true, request: item, type })}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="حذف الطلب"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {item.phone_number && (
                      <WhatsAppButton phoneNumber={item.phone_number} size="sm" variant="icon" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filterData(data).length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">لا توجد بيانات</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')}><ArrowRight className="w-6 h-6 text-white" /></button>
            <Logo size="md" />
            <h1 className="text-xl font-heading font-bold text-white">طلبات العملاء</h1>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* البحث */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="بحث بالاسم أو الرقم..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pr-10 pl-4 py-2 border border-gray-200 rounded-lg"
            />
          </div>
        </div>

        {/* التبويبات */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setActiveTab('cases')} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${activeTab === 'cases' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
            <FileText className="w-4 h-4" />
            القضايا ({cases.length})
          </button>
          <button onClick={() => setActiveTab('consultations')} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${activeTab === 'consultations' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
            <MessageSquare className="w-4 h-4" />
            الاستشارات ({consultations.length})
          </button>
          <button onClick={() => setActiveTab('notary')} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${activeTab === 'notary' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
            <PenTool className="w-4 h-4" />
            خدمات الموثق ({notaryRequests.length})
          </button>
        </div>

        {/* الجداول */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-gold animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'cases' && renderTable(cases, 'case')}
            {activeTab === 'consultations' && renderTable(consultations, 'consultation')}
            {activeTab === 'notary' && renderTable(notaryRequests, 'notary')}
          </>
        )}
      </main>

      {/* مودال التفاصيل */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-heading font-semibold text-primary">تفاصيل الطلب</h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">الرقم</p><p className="font-semibold">{selectedRequest.request_number || selectedRequest.consultation_number}</p></div>
                <div><p className="text-sm text-gray-500">العميل</p><p className="font-semibold">{selectedRequest.client_name}</p></div>
                <div><p className="text-sm text-gray-500">الجوال</p><p className="font-semibold" dir="ltr">{selectedRequest.phone_number}</p></div>
                <div><p className="text-sm text-gray-500">الحالة</p>{getStatusBadge(selectedRequest.status)}</div>
              </div>
              <div><p className="text-sm text-gray-500">الموضوع</p><p className="font-semibold">{selectedRequest.subject || selectedRequest.service_type}</p></div>
              <div><p className="text-sm text-gray-500">التفاصيل</p><p className="text-gray-700">{selectedRequest.description || selectedRequest.details || '-'}</p></div>
              {selectedRequest.phone_number && (
                <div className="pt-4 border-t">
                  <WhatsAppButton phoneNumber={selectedRequest.phone_number} clientName={selectedRequest.client_name} variant="full" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* مودال الإجراء والإحالة */}
      {showActionModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gradient-to-l from-primary to-primary-800 text-white rounded-t-xl">
              <h3 className="text-lg font-heading font-semibold">إجراء / إحالة الطلب</h3>
              <button onClick={() => setShowActionModal(false)} className="p-2 hover:bg-white/20 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* معلومات الطلب */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><span className="text-gray-500">الرقم:</span> <strong>{selectedRequest.request_number || selectedRequest.consultation_number}</strong></div>
                  <div><span className="text-gray-500">العميل:</span> <strong>{selectedRequest.client_name}</strong></div>
                  <div><span className="text-gray-500">الحالة:</span> {getStatusBadge(selectedRequest.status)}</div>
                </div>
              </div>

              {/* سجل الإجراءات السابقة */}
              {requestActions.length > 0 && (
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-3 bg-gray-50 border-b flex items-center gap-2">
                    <History className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold text-sm">سجل الإجراءات ({requestActions.length})</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto divide-y">
                    {requestActions.map((action, idx) => (
                      <div key={idx} className="p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-primary">{action.performed_by_name}</span>
                          <span className="text-xs text-gray-400">{new Date(action.created_at).toLocaleString('ar-SA')}</span>
                        </div>
                        <p className="text-gray-700">{action.action_text}</p>
                        {action.new_assigned_to_names && (
                          <p className="text-xs text-blue-600 mt-1">➜ تم الإحالة إلى: {action.new_assigned_to_names.join(', ')}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* نموذج الإجراء الجديد */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">نوع الإجراء</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'update', label: 'تحديث حالة', icon: Edit },
                      { value: 'assign', label: 'إحالة لموظف', icon: Send },
                      { value: 'transfer', label: 'نقل للإدارة', icon: Users },
                      { value: 'archive', label: 'حفظ بالأرشيف', icon: Archive }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setActionType(opt.value)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                          actionType === opt.value ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-primary'
                        }`}
                      >
                        <opt.icon className="w-4 h-4" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* تفاصيل الإجراء */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">تفاصيل الإجراء *</label>
                  <textarea
                    value={actionText}
                    onChange={(e) => setActionText(e.target.value)}
                    placeholder="اكتب ما تم إنجازه أو التعليمات للموظف التالي..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold"
                  />
                </div>

                {/* اختيار الموظفين للإحالة */}
                {(actionType === 'assign' || actionType === 'transfer') && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">إحالة إلى</label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                      {employees.map(emp => (
                        <button
                          key={emp.id}
                          onClick={() => {
                            if (selectedEmployees.find(e => e.id === emp.id)) {
                              setSelectedEmployees(selectedEmployees.filter(e => e.id !== emp.id));
                            } else {
                              setSelectedEmployees([...selectedEmployees, emp]);
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            selectedEmployees.find(e => e.id === emp.id)
                              ? 'bg-gold text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {emp.full_name}
                        </button>
                      ))}
                    </div>
                    {selectedEmployees.length > 0 && (
                      <p className="text-sm text-gold mt-2">المحدد: {selectedEmployees.map(e => e.full_name).join(', ')}</p>
                    )}
                  </div>
                )}

                {/* تغيير الحالة */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">تغيير الحالة (اختياري)</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  >
                    <option value="">بدون تغيير</option>
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* أزرار الحفظ */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={submitAction}
                  disabled={savingAction || !actionText.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark disabled:opacity-50"
                >
                  {savingAction ? <RefreshCw className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  حفظ الإجراء
                </button>
                <button
                  onClick={() => setShowActionModal(false)}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* مودال تأكيد الحذف */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-heading font-bold text-red-600">تأكيد حذف الطلب</h3>
              <button
                onClick={() => setDeleteModal({ show: false, request: null, type: null })}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-4 p-4 bg-red-50 rounded-xl mb-4">
              <AlertCircle className="w-12 h-12 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-body text-lg text-gray-700">
                  هل أنت متأكد من حذف هذا الطلب؟
                </p>
                <p className="font-bold text-lg text-red-600 mt-1">
                  {deleteModal.request?.subject || deleteModal.request?.title || 'بدون عنوان'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  العميل: {deleteModal.request?.client_name || '-'}
                </p>
              </div>
            </div>
            
            <p className="text-gray-500 text-base mb-6 font-body">
              سيتم حذف هذا الطلب وجميع بياناته نهائياً ولا يمكن استرجاعه.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal({ show: false, request: null, type: null })}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-body text-lg"
              >
                إلغاء
              </button>
              <button
                onClick={handleDeleteRequest}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-body text-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    جاري الحذف...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    حذف نهائياً
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
